import { Dispatch, SetStateAction, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Edit3, Search, Trash2 } from 'lucide-react';
import FormFieldComponent from '@/components/FormFieldComponent';
import { useFormContext } from 'react-hook-form';
import FormFieldSearch from '@/components/FormFieldSearch';
import { billingItemSearch } from '@/services/Network';
import type { DepartmentType } from '@/types/workOrder.types';
import type { ServiceType } from '@/types/types';
import { genUniqueId } from '@/lib/utils';
import { BillingAmount } from '@/components/BillingAmount';

const SearchIconsProps = {
  Icon: Search,
  iconProps: { style: { color: '#969696', height: '20px', width: '20px' } },
};

export type BillingItem = {
  id: string;
  itemId: string;
  description: string;
  amount: number;
  billingItemId?: string | null;
};

export const InvoiceBillingInformationTable = ({
  mode,
  billingItems,
  setBillingItems,
  serviceType,
  department,
}: {
  mode: 'view' | 'edit';
  billingItems: BillingItem[];
  setBillingItems: Dispatch<SetStateAction<BillingItem[]>>;
  serviceType: ServiceType | null;
  department: DepartmentType | null;
}) => {
  const {
    control,
    setValue,
    trigger,
    formState: { isValid },
  } = useFormContext();
  const [editItem, setEditItem] = useState<BillingItem | null>(null);
  const [editedAmount, setEditedAmount] = useState('');

  const handleEdit = (item: BillingItem) => {
    setEditItem(item);
    setEditedAmount(item.amount.toString());
    setValue('updatedAmount', item.amount);
  };

  const handleSave = () => {
    if (!editItem) return;
    setBillingItems((prev) =>
      prev.map((row) => (row.id === editItem.id ? { ...row, amount: parseFloat(editedAmount) } : row))
    );
    setEditItem(null);
    setValue('updatedAmount', null);
  };

  const handleCancel = () => {
    setEditItem(null);
    setValue('updatedAmount', null);
  };

  const handleDelete = (id: string) => {
    setBillingItems((prev) => prev.filter((row) => row.id !== id));
    setValue(
      'billingItems',
      billingItems.filter((item) => item.id !== id),
      { shouldDirty: true, shouldTouch: true, shouldValidate: true }
    );
  };

  return (
    <div className="p-6 bg-white">
      <div className="-mx-6 -mt-6">
        <Table className={`rounded-none ${mode === 'edit' ? 'max-h-[250px]' : 'max-h-[470px]'} custom-scrollbar`}>
          <TableHeader>
            <TableRow className="bg-[#f5f5f5] border-none">
              <TableHead className="pl-6 text-black font-bold">SN</TableHead>
              <TableHead className="text-black font-bold">Code</TableHead>
              <TableHead className="w-[50%] text-black font-bold">Description</TableHead>
              <TableHead className="text-black font-bold">Amount</TableHead>
              <TableHead className="pr-6 text-black font-bold"></TableHead>
            </TableRow>
          </TableHeader>
          {billingItems.length > 0 ? (
            <TableBody key={`${billingItems.length}`}>
              {billingItems.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell className="pl-6">{index + 1}.</TableCell>
                  <TableCell>{item.itemId}</TableCell>
                  <TableCell className="max-w-0 break-words">{item.description}</TableCell>
                  <TableCell>
                    <BillingAmount value={item.amount ?? 0} />
                  </TableCell>
                  <TableCell className="space-x-2 text-right pr-6">
                    {mode === 'edit' && (
                      <div className="flex flex-row gap-3">
                        <Trash2
                          onClick={() => handleDelete(item.id)}
                            className="w-4 h-4 text-[#D72222] cursor-pointer hover:text-[#D72222]/80 transition-colors"
                        />
                          <Edit3
                            onClick={() => handleEdit(item)}
                            className="w-4 h-4 text-primary cursor-pointer hover:text-primary/80 transition-colors"
                          />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={5} className="text-center p-8 text-placeholder">
                  No billing items found
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </div>
      {mode === 'edit' && (
        <div className="pt-3">
          <FormFieldSearch
            name="billingItemSearch"
            placeholder="Select Billing Items"
            searchFieldName="search"
            props={SearchIconsProps}
            label="Add Billing Items"
            api={(params, options) => {
              const updatedParams = {
                ...params,
                search: params.search?.trim(),
                serviceType: serviceType,
                department: department,
                pageSize: 100,
              };
              return billingItemSearch(updatedParams, options);
            }}
            onChange={async (_, option) => {
              if (option)
                setBillingItems((prev) => [
                  ...prev,
                  {
                    ...(option as BillingItem),
                    id: genUniqueId(),
                  },
                ]);
              await trigger();
              setValue('billingItemSearch', null);
            }}
          />
        </div>
      )}

      <Dialog open={!!editItem}>
        <DialogContent className="max-w-[40%]">
          <DialogHeader>
            <DialogTitle>Update Amount for {editItem?.itemId} </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <FormFieldComponent
              type="number"
              name="updatedAmount"
              control={control}
              onChange={(e) => setEditedAmount(e.target.value)}
              label="Amount"
              placeholder="Enter Amount"
              allowNegativeNumbers
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} className="h-[50px] w-[125px]">
              Cancel
            </Button>
            <Button onClick={handleSave} className="h-[50px] w-[125px]" disabled={!isValid}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
